import classNames from 'classnames';
import debounce from 'lodash.debounce';
import NextLink from 'next/link';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import ChevronSm from '../../icons/ChevronSm';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import IconRenderer, { IconDefinition } from '../IconRenderer';
import styles from './styles/styles.module.scss';

const toPx = (px: number) => `${px}px`;

const scrollOptions: ScrollIntoViewOptions = { behavior: 'smooth', block: 'nearest', inline: 'nearest' };

export type NavItemDefinition = {
  id: string;
  label: ReactNode;
  href: string;
  icon?: IconDefinition<'orientation'>;
};

export type NavBarProps = {
  activePath: string;
  ariaLabel?: string;
  className?: string;
  focusStrategy?: 'stay-on-link' | 'move-to-target';
  hideBottomBorder?: boolean;
  navItems: NavItemDefinition[];
  onNavigate?: (id: string) => void;
  onNavClick?: (e: React.MouseEvent<HTMLAnchorElement>, navItem: NavItemDefinition) => void;
};

const NavBar = ({
  activePath,
  ariaLabel,
  className,
  focusStrategy = 'move-to-target',
  hideBottomBorder,
  navItems,
  onNavigate,
  onNavClick
}: NavBarProps) => {
  const navItemRefs = useRef<{ [id: string]: HTMLAnchorElement }>({});
  const activeIndicator = useRef<HTMLDivElement>(null);
  const [activeElement, setActiveElement] = useState<HTMLAnchorElement>();
  const navBar = useRef<HTMLUListElement>(null);
  const leftArrow = useRef<HTMLButtonElement>(null);
  const rightArrow = useRef<HTMLButtonElement>(null);

  const positionActiveIndicator = useCallback(() => {
    if (activeElement && activeIndicator.current) {
      activeIndicator.current.style.width = toPx(activeElement.clientWidth);
      activeIndicator.current.style.left = toPx(activeElement.offsetLeft);

      // Apply the transition style after a short delay so that the initial positioning isn't animated
      if (!activeIndicator.current.style.transition) {
        setTimeout(() => {
          if (activeElement && activeIndicator.current) {
            activeIndicator.current.style.transition = 'all 0.4s ease-in-out';
          }
        }, 500);
      }
    }
  }, [activeElement]);

  // navItems should really only change in storybook
  useEffect(positionActiveIndicator, [positionActiveIndicator, navItems]);

  const updateArrowButtons = useCallback(() => {
    if (!navBar.current || !leftArrow.current || !rightArrow.current) {
      return;
    }
    const { clientWidth, scrollWidth, scrollLeft } = navBar.current;
    if (scrollLeft < 1) {
      leftArrow.current.style.display = 'none';
    } else {
      leftArrow.current.style.display = 'flex';
    }

    if (scrollWidth - clientWidth - scrollLeft > 1) {
      rightArrow.current.style.display = 'flex';
    } else {
      rightArrow.current.style.display = 'none';
    }
  }, []);

  const handleResize = useCallback(() => {
    positionActiveIndicator();
    updateArrowButtons();
  }, [positionActiveIndicator, updateArrowButtons]);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', debounce(handleResize, 300));

    return () => {
      window.removeEventListener('resize', debounce(handleResize, 300));
    };
  }, [handleResize]);

  // Scroll left or right by the width of the nav bar
  const onArrowClick = (right?: boolean) => {
    if (!navBar.current) {
      return;
    }
    const direction = right ? 1 : -1;
    navBar.current?.scrollBy({ behavior: 'smooth', left: direction * navBar.current.clientWidth });
  };

  const onNavItemClick = (navItem: NavItemDefinition) => {
    const element = navItemRefs.current[navItem.id];
    if (element) {
      element.scrollIntoView(scrollOptions);
    }
    onNavigate?.(navItem.id);
  };

  const renderedNavItems = navItems.map(navItem => {
    const active = navItem.id === activePath;

    const content = navItem.icon ? (
      <>
        <IconRenderer icon={navItem.icon} defaults={{ color: 'currentColor', size: 'md' }} />
        <span>{navItem.label}</span>
      </>
    ) : (
      <span>{navItem.label}</span>
    );

    return (
      <li key={navItem.id} className={styles.navItem}>
        <NextLink
          href={navItem.href}
          className={classNames(styles.navLink, active && styles.active)}
          onClick={e => {
            // Use custom handler only for move-to-target strategy when provided
            if (focusStrategy === 'move-to-target' && onNavClick) {
              onNavClick(e, navItem);
            } else {
              // Default: navigate and let focus stay on link
              onNavItemClick(navItem);
            }
          }}
          onKeyDown={e => {
            const element = e.currentTarget;
            if (e.key === ' ' && element instanceof HTMLElement) {
              e.preventDefault();
              element.click();
            }
          }}
          ref={el => {
            if (el) {
              if (active) {
                setActiveElement(el);
              }
              navItemRefs.current[navItem.id] = el;
            }
          }}
          aria-current={active ? 'page' : undefined}
          prefetch={false}
        >
          {content}
        </NextLink>
      </li>
    );
  });

  return (
    <nav
      {...setDenaliAttribute('NavBar')}
      className={classNames(className, styles.container, hideBottomBorder && styles.hideBottomBorder)}
      aria-label={ariaLabel}
    >
      <ul className={styles.navBar} onScroll={debounce(updateArrowButtons, 100)} ref={navBar}>
        {renderedNavItems}
        <div className={styles.activeIndicator} ref={activeIndicator} aria-hidden={true} />
      </ul>
      <button className={styles.leftArrow} onClick={() => onArrowClick()} ref={leftArrow} tabIndex={-1} aria-hidden type="button">
        <ChevronSm orientation="left" color="--color-text-primary" />
      </button>
      <button className={styles.rightArrow} onClick={() => onArrowClick(true)} ref={rightArrow} tabIndex={-1} aria-hidden type="button">
        <ChevronSm orientation="right" color="--color-text-primary" />
      </button>
    </nav>
  );
};

export default NavBar;
